<template>
	<div class="amelipro-illustrated-radio-group">
		<div>
			<div class="d-flex align-center mr-2 amelipro-illustrated-radio-group__label-wrapper">
				<p
					:id="groupId"
					class="d-inline-flex mb-0 amelipro-illustrated-radio-group__label"
				>
					{{ groupLabel }}

					<span
						v-if="ariaRequired"
						aria-hidden="true"
						class="d-inline-flex"
					>
						&nbsp;*
					</span>
					<span
						v-if="ariaRequired"
						class="d-sr-only"
					>
						&nbsp;Champ obligatoire
					</span>
				</p>

				<slot name="labelInfo" />
			</div>

			<div
				:aria-labelledby="groupId"
				:aria-required="ariaRequired"
				class="mt-2 amelipro-illustrated-radio-group__group"
				role="radiogroup"
			>
				<ul class="d-flex flex-wrap list-style-none amelipro-illustrated-radio-group__list">
					<li
						v-for="(item, index) in items"
						:key="index"
						class="amelipro-illustrated-radio-group__item"
					>
						<label
							class="mr-2 mb-2 d-inline-flex flex-row align-center amelipro-illustrated-radio-group__item__label"
						>
							<input
								:id="`${groupId}-input-${index}`"
								v-model="selectedValue"
								:aria-describedby="checkErrorCondition ? `${groupId}-error` : undefined"
								:disabled="disabled"
								:name="`${groupId}-name`"
								:required="ariaRequired"
								type="radio"
								:value="item.value"
								@change.stop.prevent="emitChangeEvent(item)"
							>

							<span
								class="d-flex flex-column align-center amelipro-illustrated-radio-group__item__label__content"
							>
								<span
									class="d-flex flex-column align-center pa-4 icon-radio__wrapper"
									:class="selectedValue === item.value ? 'ap-blue darken-1' : 'ap-blue lighten-3'"
								>
									<AmeliproIcon
										:icon="item.icon"
										icon-bg-color="transparent"
										:icon-color="selectedValue === item.value ? 'ap-white' : item.iconDefaultColor"
										:size="iconSize"
									/>

									<span
										class="mt-4 amelipro-illustrated-radio-group__item__label__text"
										:class="selectedValue === item.value ? 'ap-white--text' : 'ap-blue--text text--darken-1'"
									>
										{{ item.label }}
									</span>
								</span>
							</span>
						</label>
					</li>
				</ul>
			</div>
		</div>

		<AmeliproMessage
			v-if="checkErrorCondition"
			:id="`${groupId}-error`"
			no-icon
			text
			type="error"
		>
			<p class="mb-0">
				{{ locales.errorMessage }}
			</p>
		</AmeliproMessage>
	</div>
</template>

<script setup lang="ts">
	import { PropType, computed, ref, watch } from 'vue';
	import AmeliproIcon from '../AmeliproIcon';
	import { AmeliproIllustratedRadioGroupItem } from './types';
	import AmeliproMessage from '../AmeliproMessage';
	import { locales } from './locales';

	const props = defineProps({
		ariaRequired: {
			type: Boolean,
			default: false,
		},
		defaultChecked: {
			type: String,
			default: undefined,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		error: {
			type: Boolean,
			default: false,
		},
		groupId: {
			type: String,
			required: true,
		},
		groupLabel: {
			type: String,
			required: true,
		},
		iconSize: {
			type: String,
			default: '40px',
		},
		items: {
			type: Array as PropType<AmeliproIllustratedRadioGroupItem[]>,
			required: true,
		},

	});

	const selectedValue = ref<string | null>(props.defaultChecked ?? null);
	watch(() => props.defaultChecked, (newValue: string | undefined) => {
		selectedValue.value = newValue ?? null;
	});

	const emit = defineEmits(['input']);
	const emitChangeEvent = (item: AmeliproIllustratedRadioGroupItem): void => {
		selectedValue.value = item.value ?? null;
		emit('input', selectedValue.value);
	};

	const checkErrorCondition = computed<boolean>(() => props.ariaRequired && selectedValue.value === null && props.error);

</script>

<style lang="scss" scoped>
.icon-radio__wrapper {
	border-radius: 10px;
}

input {
	position: absolute;
	width: 0;
	height: 0;
	opacity: 0;

	&:focus {
		&+span {
			outline: 1px dotted $ap-grey-darken1;
		}
	}

	&+span {
		position: relative;
		padding-bottom: 2.125rem;
		cursor: pointer;

		&::before {
			position: absolute;
			bottom: 3px;
			left: calc(50% - 0.8125rem);
			width: 1.625rem;
			height: 1.625rem;
			border-radius: 50%;
			border: 3px solid $ap-blue-darken1;
			background-color: transparent;
			content: "";
		}
	}

	&:checked {

		&+span {

			&::after {
				position: absolute;
				bottom: 0.578rem;
				left: calc(50% - 0.4rem);
				width: 0.825rem;
				height: 0.825rem;
				border-radius: 50%;
				background-color: $ap-blue-darken1;
				content: "";
			}
		}
	}

	&:disabled {

		&+span {
			cursor: default;

			&::before {
				opacity: 0.3;
			}
		}
	}
}

.amelipro-illustrated-radio-group__item__label__text {
	font-weight: $label-font-weight;
}
</style>
